import { CompanyData } from '@/features/company/types/company.type';
import {
  AreaChart,
  BookX,
  CarFront,
  Coins,
  DollarSign,
  FileBadgeIcon,
  FileInput,
  FileSpreadsheet,
  FileStack,
  FileStackIcon,
  FileText,
  Fuel,
  GitFork,
  HandshakeIcon,
  PieChart,
  Receipt,
  Repeat2,
  SirenIcon,
  Table,
  TableProperties,
  TicketIcon,
  UserRound,
  WrenchIcon,
} from 'lucide-react';

type Route = {
  label: string;
  Icon: JSX.Element;
  path: string | Route[];
  permission?: string;
  showOnlyWithCompanyKind?: string;
  hidden?: boolean;
}

const routes: ({ company }: { company?: CompanyData }) => Route[] = ({ company }) => [
  {
    label: 'Dados gerais',
    Icon: <PieChart strokeWidth={1} />,
    path: '/company',
    permission: 'Client::Companies::Mine',
  },
  {
    label: 'Relatórios',
    Icon: <FileSpreadsheet strokeWidth={1} />,
    path: '/reports',
    permission: 'Client::Reports::Entries::PermittedReportsFilter',
  },
  {
    label: 'Tabelas ANP',
    Icon: <Table strokeWidth={1} />,
    path: '/anp-reports',
    permission: 'Client::Anp::Reports::Filter',
  },
  {
    label: 'Tabelas Série Histórica Mensal ANP',
    Icon: <Table strokeWidth={1} />,
    path: '/anp-historical-reports',
    permission: 'Client::Anp::HistoricalReports::Filter',
  },
  {
    label: 'Negociações',
    Icon: <HandshakeIcon strokeWidth={1} />,
    path: '/negotiations',
    permission: 'Client::Negotiations::Filter',
    showOnlyWithCompanyKind: 'private_company',
  },
  {
    label: 'Recolha de notas fiscais',
    Icon: <Repeat2 strokeWidth={1} />,
    path: [
      {
        label: 'Por transações',
        Icon: <Coins strokeWidth={1} />,
        path: '/events-with-fiscal-document-requirement',
        permission: 'Client::Billing::FiscalDocumentGroups::Filter',
      },
      {
        label: 'Por lote',
        Icon: <TableProperties strokeWidth={1} />,
        path: '/billing-fiscal-document-groups',
        permission: 'Client::Billing::FiscalDocumentGroups::Filter',
      },
    ],
  },
  {
    label: 'Estrutura',
    Icon: <GitFork strokeWidth={1} />,
    path: '/company-branches',
    permission: 'Client::CompanyBranches::Filter',
  },
  {
    label: 'Empenhos',
    Icon: <FileStack strokeWidth={1} />,
    path: '/government-allocations',
    permission: 'Client::Billing::GovernmentAllocations::Filter',
    showOnlyWithCompanyKind: 'public_company',
  },
  {
    label: 'Documentos / Certidões',
    Icon: <FileBadgeIcon strokeWidth={1} />,
    path: '/general-documents',
    permission: 'Client::GeneralDocuments::Filter',
    showOnlyWithCompanyKind: 'public_company',
  },
  {
    label: 'AF',
    Icon: <FileStackIcon strokeWidth={1} />,
    path: '/supply-authorizations',
    permission: 'Client::Billing::SupplyAuthorizations::Filter',
    showOnlyWithCompanyKind: 'public_company',
  },
  {
    label: 'Financeiro',
    Icon: <Coins strokeWidth={1} />,
    path: [
      {
        label: 'Transações',
        Icon: <Receipt strokeWidth={1} />,
        path: '/billing-events',
        permission: 'Client::Billing::Events::Filter',
      },
      {
        label: 'Transações falhas',
        Icon: <Receipt strokeWidth={1} />,
        path: '/failed-transactions',
        permission: 'Client::Consumptions::Filter',
      },
      {
        label: 'Totalizadores',
        Icon: <AreaChart strokeWidth={1} />,
        path: '/movements-totalizer',
        permission: 'Client::Companies::MovementsTotalizer',
      },
      {
        label: 'Faturas',
        Icon: <FileText strokeWidth={1} />,
        path: '/billing-bills',
        permission: 'Client::Billing::Bills::Filter',
      },
      {
        label: 'Depósitos',
        Icon: <FileInput strokeWidth={1} />,
        path: '/banking-deposits',
        permission: 'Client::Banking::Deposits::Filter',
        showOnlyWithCompanyKind: 'private_company',
      },
    ],
  },
  {
    label: 'Rede credenciada',
    Icon: <Fuel strokeWidth={1} />,
    path: '/establishments',
    permission: 'Client::Establishment::Companies::GeoClustered',
  },
  {
    label: 'Pesquisa de preços',
    Icon: <DollarSign strokeWidth={1} />,
    path: '/price-survey',
    permission: 'Client::PriceSurveys::Filter',
    hidden: company?.price_survey_mode === 'disabled',
  },
  {
    label: 'Equipamentos',
    Icon: <CarFront strokeWidth={1} />,
    path: '/equipments',
    permission: 'Client::Equipments::Filter',
  },
  {
    label: 'Manutenções',
    Icon: <WrenchIcon strokeWidth={1} />,
    path: [
      {
        label: 'Processos de manutenção',
        Icon: <WrenchIcon strokeWidth={1} />,
        path: '/maintenance-workflows',
        permission: 'Client::Maintenance::Workflows::Filter',
      },
      {
        label: 'Solicitações de manutenção',
        Icon: <SirenIcon strokeWidth={1} />,
        path: '/maintenance-solicitations',
        permission: 'Client::Maintenance::Solicitations::Filter',
      },
      {
        label: 'Regras de desfazimento',
        Icon: <BookX strokeWidth={1} />,
        path: '/maintenance-scrap-rules',
        permission: 'Client::Maintenance::ScrapRules::Filter',
      },
    ],
  },
  {
    label: 'Usuários',
    Icon: <UserRound strokeWidth={1} />,
    path: '/users',
    permission: 'Client::Users::Filter',
  },
  {
    label: 'Chamados',
    Icon: <TicketIcon strokeWidth={1} />,
    path: '/support',
    permission: 'Client::Support::Tickets::Filter',
  },
]

const flattenRoutes = (items: Route[]): Route[] => items.flatMap((item) => {
  if (Array.isArray(item.path))
    return flattenRoutes(item.path)

  if (typeof item.path === 'string')
    return { ...item }

  return [];
})

export const flattedRoutes = ({ company }: { company?: CompanyData }) => flattenRoutes(routes({ company }))

export default routes
